"use strict";
import {
  useCreateWallet,
  useExportWallet,
  useLoginWithOAuth,
  useLogout,
  useMfaEnrollment,
  usePrivy,
  useWallets
} from "@privy-io/react-auth";
import { PRIVY_CONNECTOR_ID, privyEmbeddedConnector } from "components/Web3Provider/privy/privyConnector";
import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useAccount, useConnect, useDisconnect } from "wagmi";
import { bsc } from "wagmi/chains";
const PrivyAuthContext = createContext(null);
export function usePrivyAuth() {
  return useContext(PrivyAuthContext);
}
export function PrivyWagmiBridge({ children }) {
  const { ready, authenticated, user } = usePrivy();
  const { logout: privyLogout } = useLogout();
  const { createWallet } = useCreateWallet();
  const { exportWallet } = useExportWallet();
  const { showMfaEnrollmentModal } = useMfaEnrollment();
  const { wallets } = useWallets();
  const { connectAsync } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { connector, isConnected } = useAccount();
  const linking = useRef(false);
  const creating = useRef(false);
  const { initOAuth, loading: oauthLoading } = useLoginWithOAuth({
    onComplete: () => {
      if (isConnected && connector?.id !== PRIVY_CONNECTOR_ID) {
        void disconnectAsync();
      }
    }
  });
  const embedded = wallets.find((w) => w.connectorType === "embedded");
  const embeddedAddress = embedded?.address;
  const userHasEmbedded = Boolean(
    user?.linkedAccounts?.some((a) => a.type === "wallet" && a.connectorType === "embedded")
  );
  useEffect(() => {
    if (!ready || !authenticated) {
      return;
    }
    if (embedded || userHasEmbedded || creating.current) {
      return;
    }
    creating.current = true;
    void (async () => {
      try {
        await createWallet();
      } catch (err) {
        const benign = err instanceof Error && err.message.toLowerCase().includes("already has an embedded wallet");
        if (!benign) {
          console.error("Privy embedded wallet creation failed", err);
        }
      } finally {
        creating.current = false;
      }
    })();
  }, [ready, authenticated, embedded, userHasEmbedded, createWallet]);
  useEffect(() => {
    if (!ready || !authenticated || !embedded) {
      return;
    }
    if (isConnected || linking.current) {
      return;
    }
    linking.current = true;
    void (async () => {
      try {
        await embedded.switchChain(bsc.id);
        const provider = await embedded.getEthereumProvider();
        await connectAsync({ connector: privyEmbeddedConnector(provider), chainId: bsc.id });
      } catch (err) {
        console.error("Failed to link Privy embedded wallet to wagmi", err);
      } finally {
        linking.current = false;
      }
    })();
  }, [ready, authenticated, embedded, embeddedAddress, isConnected, connectAsync]);
  useEffect(() => {
    if (!ready) {
      return;
    }
    if (!authenticated && isConnected && connector?.id === PRIVY_CONNECTOR_ID) {
      void disconnectAsync();
    }
  }, [ready, authenticated, isConnected, connector?.id, disconnectAsync]);
  const logout = useCallback(async () => {
    if (connector?.id === PRIVY_CONNECTOR_ID) {
      await disconnectAsync();
    }
    await privyLogout();
  }, [connector?.id, disconnectAsync, privyLogout]);
  const loginWithGoogle = useCallback(() => initOAuth({ provider: "google" }), [initOAuth]);
  const exportPrivateKey = useCallback(async () => {
    if (!embeddedAddress) {
      return;
    }
    await exportWallet({ address: embeddedAddress });
  }, [exportWallet, embeddedAddress]);
  return <PrivyAuthContext.Provider
    value={{
      ready,
      authenticated,
      userId: user?.id,
      email: user?.email?.address,
      logout,
      embeddedAddress,
      loginWithGoogle,
      oauthLoading,
      exportPrivateKey,
      mfaMethods: user?.mfaMethods ?? [],
      enrollMfa: showMfaEnrollmentModal
    }}
  >
      {children}
    </PrivyAuthContext.Provider>;
}
